'use client';

import { useEffect, useRef, useState } from 'react';
import { A2_COLOR } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

/** Negative slope used in A2's leaky ReLU. */
const ALPHA = 0.01;
const N = 320;
const H1 = 24;
const H2 = 24;
const LR = 0.02;
const WARMUP_EPOCHS = 150;
const TOTAL_EPOCHS = 250;
const STEPS_PER_SEC = 60;

const TARGET_COLOR = 'rgba(160,160,168,1)';
const PRED_COLOR = A2_COLOR;

type Phase = 'idle' | 'running' | 'paused' | 'done';

type Net = {
  W1: number[];
  b1: number[];
  W2: number[][];
  b2: number[];
  W3: number[];
  b3: number;
  m: {
    W1: number[];
    b1: number[];
    W2: number[][];
    b2: number[];
    W3: number[];
    b3: number;
  };
  v: {
    W1: number[];
    b1: number[];
    W2: number[][];
    b2: number[];
    W3: number[];
    b3: number;
  };
  t: number;
};

type SimState = {
  input: number[];
  target: number[];
  net: Net;
  pred: number[];
  esr: number;
  step: number;
  acc: number;
  lastTs: number;
};

function buildSignal() {
  const input: number[] = [];
  const target: number[] = [];
  for (let i = 0; i < N; i++) {
    const t = i / N;
    const x =
      0.42 * Math.sin(2 * Math.PI * 2 * t) +
      0.26 * Math.sin(2 * Math.PI * 5 * t + 1.0) +
      0.17 * Math.sin(2 * Math.PI * 9 * t + 2.3) +
      0.12 * Math.sin(2 * Math.PI * 13 * t + 0.7) +
      0.08 * Math.sin(2 * Math.PI * 17 * t + 1.9);
    const y = Math.tanh(8 * x + 0.6) * 0.78 + 0.05 * Math.sin(6 * x);
    input.push(x);
    target.push(y);
  }
  const mean = target.reduce((a, b) => a + b, 0) / N;
  for (let i = 0; i < N; i++) target[i] -= mean;
  const tmax = Math.max(...target.map(Math.abs));
  for (let i = 0; i < N; i++) target[i] /= tmax;
  return { input, target };
}

function makeNet(seed: number): Net {
  let s = seed;
  const rnd = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return (s / 0x7fffffff) * 2 - 1;
  };
  const he = (fanIn: number) => Math.sqrt(2 / fanIn);
  const zeros1 = (n: number) => new Array(n).fill(0);
  const zeros2 = (r: number, c: number) =>
    Array.from({ length: r }, () => new Array(c).fill(0));
  return {
    W1: Array.from({ length: H1 }, () => rnd() * he(1)),
    b1: zeros1(H1),
    W2: Array.from({ length: H2 }, () =>
      Array.from({ length: H1 }, () => rnd() * he(H1)),
    ),
    b2: zeros1(H2),
    W3: Array.from({ length: H2 }, () => rnd() * he(H2)),
    b3: 0,
    m: {
      W1: zeros1(H1),
      b1: zeros1(H1),
      W2: zeros2(H2, H1),
      b2: zeros1(H2),
      W3: zeros1(H2),
      b3: 0,
    },
    v: {
      W1: zeros1(H1),
      b1: zeros1(H1),
      W2: zeros2(H2, H1),
      b2: zeros1(H2),
      W3: zeros1(H2),
      b3: 0,
    },
    t: 0,
  };
}

function predict(net: Net, input: number[], out: number[]) {
  for (let i = 0; i < N; i++) {
    const x = input[i];
    let y = net.b3;
    const a1 = new Array(H1);
    for (let j = 0; j < H1; j++) {
      const z = net.W1[j] * x + net.b1[j];
      a1[j] = z > 0 ? z : ALPHA * z;
    }
    for (let k = 0; k < H2; k++) {
      let s = net.b2[k];
      for (let j = 0; j < H1; j++) s += net.W2[k][j] * a1[j];
      const a2 = s > 0 ? s : ALPHA * s;
      y += net.W3[k] * a2;
    }
    out[i] = y;
  }
}

function lrFor(step: number) {
  const w = Math.min(1, (step + 1) / WARMUP_EPOCHS);
  return LR * w * w;
}

/** One full-batch Adam step. Returns ESR. */
function trainStep(net: Net, input: number[], target: number[], lr: number) {
  const gW1 = new Array(H1).fill(0);
  const gb1 = new Array(H1).fill(0);
  const gW2 = Array.from({ length: H2 }, () => new Array(H1).fill(0));
  const gb2 = new Array(H2).fill(0);
  const gW3 = new Array(H2).fill(0);
  let gb3 = 0;

  let se = 0;
  let te = 0;
  const z1 = new Array(H1);
  const a1 = new Array(H1);
  const z2 = new Array(H2);
  const a2 = new Array(H2);

  for (let i = 0; i < N; i++) {
    const x = input[i];
    const yt = target[i];
    for (let j = 0; j < H1; j++) {
      z1[j] = net.W1[j] * x + net.b1[j];
      a1[j] = z1[j] > 0 ? z1[j] : ALPHA * z1[j];
    }
    for (let k = 0; k < H2; k++) {
      let s = net.b2[k];
      for (let j = 0; j < H1; j++) s += net.W2[k][j] * a1[j];
      z2[k] = s;
      a2[k] = s > 0 ? s : ALPHA * s;
    }
    let y = net.b3;
    for (let k = 0; k < H2; k++) y += net.W3[k] * a2[k];

    const err = y - yt;
    se += err * err;
    te += yt * yt;
    const dy = (2 * err) / N;

    gb3 += dy;
    const da1 = new Array(H1).fill(0);
    for (let k = 0; k < H2; k++) {
      gW3[k] += dy * a2[k];
      const dz2 = dy * net.W3[k] * (z2[k] > 0 ? 1 : ALPHA);
      gb2[k] += dz2;
      for (let j = 0; j < H1; j++) {
        gW2[k][j] += dz2 * a1[j];
        da1[j] += dz2 * net.W2[k][j];
      }
    }
    for (let j = 0; j < H1; j++) {
      const dz1 = da1[j] * (z1[j] > 0 ? 1 : ALPHA);
      gW1[j] += dz1 * x;
      gb1[j] += dz1;
    }
  }

  net.t++;
  const beta1 = 0.9;
  const beta2 = 0.999;
  const eps = 1e-8;
  const bc1 = 1 - Math.pow(beta1, net.t);
  const bc2 = 1 - Math.pow(beta2, net.t);
  const adam = (P: number[], G: number[], M: number[], V: number[]) => {
    for (let i = 0; i < P.length; i++) {
      M[i] = beta1 * M[i] + (1 - beta1) * G[i];
      V[i] = beta2 * V[i] + (1 - beta2) * G[i] * G[i];
      P[i] -= (lr * (M[i] / bc1)) / (Math.sqrt(V[i] / bc2) + eps);
    }
  };
  adam(net.W1, gW1, net.m.W1, net.v.W1);
  adam(net.b1, gb1, net.m.b1, net.v.b1);
  for (let k = 0; k < H2; k++) adam(net.W2[k], gW2[k], net.m.W2[k], net.v.W2[k]);
  adam(net.b2, gb2, net.m.b2, net.v.b2);
  adam(net.W3, gW3, net.m.W3, net.v.W3);
  net.m.b3 = beta1 * net.m.b3 + (1 - beta1) * gb3;
  net.v.b3 = beta2 * net.v.b3 + (1 - beta2) * gb3 * gb3;
  net.b3 -= (lr * (net.m.b3 / bc1)) / (Math.sqrt(net.v.b3 / bc2) + eps);

  return se / te;
}

function formatEsr(esr: number, step: number) {
  if (step === 0) return '–';
  return esr >= 0.01 ? esr.toFixed(3) : esr.toFixed(5);
}

function formatMatch(esr: number, step: number) {
  if (step === 0) return '–';
  return `${Math.min(99.99, Math.max(0, (1 - esr) * 100)).toFixed(2)}%`;
}

export default function A2TrainingDemo() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stateRef = useRef<SimState | null>(null);
  const rafRef = useRef<number | null>(null);
  const epochRef = useRef<HTMLSpanElement | null>(null);
  const esrRef = useRef<HTMLSpanElement | null>(null);
  const matchRef = useRef<HTMLSpanElement | null>(null);
  const phaseRef = useRef<Phase>('idle');
  const [phase, setPhase] = useState<Phase>('idle');

  const setPhaseBoth = (next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  function initState() {
    const { input, target } = buildSignal();
    const net = makeNet(((Date.now() % 100000) | 1) * 7919 + 42);
    const pred = new Array(N).fill(0);
    predict(net, input, pred);
    stateRef.current = {
      input,
      target,
      net,
      pred,
      esr: 1,
      step: 0,
      acc: 0,
      lastTs: 0,
    };
  }

  function updateReadouts() {
    const s = stateRef.current;
    if (!s) return;
    if (epochRef.current) epochRef.current.textContent = String(s.step);
    if (esrRef.current) esrRef.current.textContent = formatEsr(s.esr, s.step);
    if (matchRef.current) matchRef.current.textContent = formatMatch(s.esr, s.step);
  }

  function draw() {
    const canvas = canvasRef.current;
    const s = stateRef.current;
    if (!canvas || !s) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const padL = 12;
    const padR = 12;
    const padT = 34;
    const padB = 14;
    const plotW = w - padL - padR;
    const plotH = h - padT - padB;
    const yMax = 1.25;
    const toX = (i: number) => padL + (i / (N - 1)) * plotW;
    const toY = (v: number) => padT + (1 - (v + yMax) / (2 * yMax)) * plotH;

    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, toY(0));
    ctx.lineTo(w - padR, toY(0));
    ctx.stroke();

    ctx.strokeStyle = 'rgba(128,128,128,0.15)';
    for (const g of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(padL, toY(g));
      ctx.lineTo(w - padR, toY(g));
      ctx.stroke();
    }

    const line = (data: number[], color: string, width: number) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 0; i < N; i++) {
        const x = toX(i);
        const y = toY(data[i]);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    };

    line(s.target, TARGET_COLOR, 2.5);
    line(s.pred, PRED_COLOR, 2.5);

    ctx.font = 'bold 13px Arial, sans-serif';
    ctx.textBaseline = 'middle';
    let lx = padL + 2;
    const ly = 14;
    const swatch = (color: string, label: string) => {
      ctx.fillStyle = color;
      ctx.fillRect(lx, ly - 5, 18, 3.5);
      lx += 24;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.textAlign = 'left';
      ctx.fillText(label, lx, ly - 3);
      lx += ctx.measureText(label).width + 22;
    };
    swatch(TARGET_COLOR, 'Real amp');
    swatch(PRED_COLOR, 'Prediction');
  }

  function loop(ts: number) {
    const s = stateRef.current;
    if (!s) return;
    if (!s.lastTs) s.lastTs = ts;
    s.acc += Math.min(ts - s.lastTs, 100);
    s.lastTs = ts;

    const stepMs = 1000 / STEPS_PER_SEC;
    let finished = false;
    while (s.acc >= stepMs && !finished) {
      s.acc -= stepMs;
      s.esr = trainStep(s.net, s.input, s.target, lrFor(s.step));
      s.step++;
      if (s.step >= TOTAL_EPOCHS) finished = true;
    }
    predict(s.net, s.input, s.pred);
    draw();
    updateReadouts();

    if (finished) {
      setPhaseBoth('done');
      rafRef.current = null;
      return;
    }
    rafRef.current = requestAnimationFrame(loop);
  }

  function play() {
    if (phaseRef.current === 'done') return;
    const s = stateRef.current;
    if (s) {
      s.lastTs = 0;
      s.acc = 0;
    }
    setPhaseBoth('running');
    rafRef.current = requestAnimationFrame(loop);
  }

  function pause() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setPhaseBoth('paused');
  }

  function reset() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    initState();
    draw();
    updateReadouts();
    setPhaseBoth('idle');
  }

  useEffect(() => {
    initState();
    draw();
    updateReadouts();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="Watch Leaky ReLU Learn a High-Gain Amp"
        description="A tiny neural network with leaky ReLU activations, training live in your browser. The gray line is a high-gain amp's output; the yellow line is the network's prediction. It starts as noise and learns the amp's response through gradient descent, the same process that trains a NAM capture."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
        <div className="text-lg font-bold text-white">Real Amp vs Prediction</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">
          Trained with leaky ReLU (α = {ALPHA}) for {TOTAL_EPOCHS} epochs, ESR measured live
        </div>
        <div className="relative h-[340px] w-full max-[500px]:h-[280px]">
          <canvas ref={canvasRef} className="block h-full w-full" />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#373739] pt-4 text-xs">
          <div className="flex items-center gap-1.5">
            {phase === 'running' ? (
              <button
                type="button"
                onClick={pause}
                className="rounded-md bg-[#FFFF00]/15 px-3 py-1 font-bold text-[#FFFF00] transition hover:bg-[#FFFF00]/25"
              >
                Pause
              </button>
            ) : phase === 'done' ? (
              <button
                type="button"
                onClick={reset}
                className="rounded-md bg-[#FFFF00]/15 px-3 py-1 font-bold text-[#FFFF00] transition hover:bg-[#FFFF00]/25"
              >
                Reset
              </button>
            ) : (
              <button
                type="button"
                onClick={play}
                className="rounded-md bg-[#FFFF00]/15 px-3 py-1 font-bold text-[#FFFF00] transition hover:bg-[#FFFF00]/25"
              >
                Train
              </button>
            )}
            {phase !== 'done' && phase !== 'idle' && (
              <button
                type="button"
                onClick={reset}
                className="rounded-md bg-white/5 px-3 py-1 text-zinc-400 transition hover:text-white"
              >
                Reset
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span>
              <span className="text-zinc-500">Epoch </span>
              <span
                ref={epochRef}
                className="inline-block min-w-[3.5rem] tabular-nums text-white/90"
              >
                0
              </span>
            </span>
            <span>
              <span className="text-zinc-500">ESR </span>
              <span
                ref={esrRef}
                className="inline-block min-w-[3.5rem] tabular-nums text-white/90"
              >
                –
              </span>
            </span>
            <span>
              <span className="text-zinc-500">Match </span>
              <span
                ref={matchRef}
                className="inline-block min-w-[3.5rem] tabular-nums text-white/90"
              >
                –
              </span>
            </span>
            {phase === 'done' && (
              <span className="font-bold text-[#FFFF00]">Trained</span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
